import { boardOf, chessColorOf, legalChessMoves, premoveChessMoves, toMove, type ChessColor, type ChessMove, type PlayerId, type PromotionPiece, type Square, type State } from '@chessshooter/shared';
import type { BoardPointerTarget } from '../input';
import type { Viewport } from '../camera';
import type { BoardScene } from '../render';
import { BoardInput } from './boardInput';
import { BoardDrawings } from './boardDrawing';
import { ChessEntryCue } from './entryCue';
import { layoutBoard, squareAt, type BoardLayout } from './boardLayout';
import { positionOf, type Position } from './position';

/** What the controller needs from the screen: where the pointers go and the promotion picker. */
export interface BoardHost {
  setBoard(target: BoardPointerTarget | null): void;
  showPromotion(color: ChessColor, onPick: (piece: PromotionPiece) => void, onCancel: () => void): void;
  hidePromotion(): void;
  playSelection?(): void;
}

export interface BoardControllerOptions {
  margin: number;
  statusHeight: number;
}

/**
 * The glue between a drawn state and the board on screen, the same in the local and the online mode:
 * the layout, the position read off the FEN once, the move state machine, the pointer routing and the
 * promotion picker. `sync()` runs once per frame with the state that is drawn; a completed move waits
 * in `take()` for the next tick's input. In the online mode that state is the prediction, so a move
 * shows at once. A held premove survives the opponent's reply with updated legal targets.
 */
export class BoardController {
  readonly input = new BoardInput();
  private readonly drawings = new BoardDrawings();
  layout: BoardLayout | null = null;
  private position: Position | null = null;
  private state: State | null = null;
  private promotionShown = false;
  private lastPhase: State['phase'] | null = null;
  private premove: ChessMove | null = null;
  private lastFen: string | null = null;
  private entryCue = new ChessEntryCue();
  private entryActive = false;

  readonly target: BoardPointerTarget = {
    pointerDown: (s) => {
      if (this.layout && s.isPrimary !== false) {
        const square = squareAt(this.layout, s);
        if (s.kind === 'mouse' && s.button === 2) {
          if (square && !((s.buttons ?? 0) & 1) && !this.input.view().drag) {
            this.input.cancel();
            this.drawings.start(s.id, square);
          }
          return;
        }
        if (s.button !== undefined && s.button !== 0) return;
        this.drawings.clear();
        this.premove = null;
        this.input.take();
        const selected = this.input.view().selected;
        this.input.pointerDown(s.id, square, s, this.legalFrom);
        if (this.input.view().selected && this.input.view().selected !== selected) this.host.playSelection?.();
      }
    },
    pointerMove: (s) => {
      if (this.layout) {
        if (s.kind === 'mouse' && ((s.buttons ?? 0) & 1)) this.drawings.clear();
        this.drawings.move(s.id, squareAt(this.layout, s));
        this.input.pointerMove(s.id, squareAt(this.layout, s), s);
      }
    },
    pointerUp: (s) => {
      if (this.layout) {
        this.drawings.finish(s.id, squareAt(this.layout, s));
        this.input.pointerUp(s.id, squareAt(this.layout, s));
      }
    },
    pointerCancel: (id) => { this.input.pointerCancel(id); this.drawings.cancel(id); },
    cancelGesture: () => { this.input.cancel(); this.drawings.cancel(); },
    clearDrawings: () => this.drawings.clear(),
  };

  constructor(
    private readonly me: PlayerId,
    private readonly host: BoardHost,
    private readonly opts: BoardControllerOptions,
  ) {}

  private readonly legalFrom = (from: Square): ChessMove[] => {
    const s = this.state;
    if (!s || s.winner !== null || s.phase !== 'chess') return [];
    if (toMove(s) === this.me) return legalChessMoves(s, from);
    return this.entryActive ? [] : premoveChessMoves(s, this.me, from);
  };

  /**
   * Keeps everything in step with the state that is drawn; returns what the renderer needs, null in the
   * arena. `active` = false shows the board but takes no pointers (the match is over).
   */
  sync(state: State, viewport: Viewport, topInset: number, active = true): BoardScene | null {
    this.update(state, active);
    const atBoard = state.phase === 'chess' && state.transitionTicks === 0;
    this.layout = atBoard ? layoutBoard(viewport, chessColorOf(state, this.me) === 'b', { topInset, margin: this.opts.margin, statusHeight: this.opts.statusHeight }) : null;
    if (atBoard) this.position = positionOf(state, this.position);
    this.host.setBoard(atBoard && active && state.winner === null ? this.target : null);
    const promotion = active ? this.input.view().promotion : null;
    if (promotion && !this.promotionShown) {
      this.promotionShown = true;
      this.host.showPromotion(
        chessColorOf(state, this.me),
        (piece) => this.input.choosePromotion(piece),
        () => this.input.cancel(),
      );
    } else if (!promotion && this.promotionShown) {
      this.promotionShown = false;
      this.host.hidePromotion();
    }
    return this.layout && this.position ? { layout: this.layout, position: this.position, input: { ...this.input.view(), premove: this.premove }, drawings: this.drawings.view() } : null;
  }

  private update(state: State, active = true): void {
    this.entryActive = this.entryCue.update(state) !== null;
    if (!active || state.winner !== null || state.phase !== 'chess' || state.transitionTicks > 0 || (this.lastPhase !== null && state.phase !== this.lastPhase)) {
      this.input.reset();
      this.premove = null;
      this.drawings.clear();
    } else if (this.entryActive && toMove(state) !== this.me) {
      this.input.reset();
      this.premove = null;
    } else if (state.chess.fen !== this.lastFen) {
      const drag = this.input.view().drag;
      const opponentMoved = this.state !== null && toMove(this.state) !== this.me && toMove(state) === this.me;
      if (drag && opponentMoved && boardOf(state).some((row) => row.some((piece) => piece?.square === drag.from && piece.color === chessColorOf(state, this.me)))) {
        this.input.refreshMoves((from) => legalChessMoves(state, from));
      } else {
        this.input.cancel();
      }
    }
    this.state = state;
    this.lastPhase = state.phase;
    this.lastFen = state.chess.fen;
    const move = this.input.take();
    if (move) this.premove = move;
  }

  /** Validate against the tick's current position, including ticks between rendered frames. */
  take(state = this.state): ChessMove | undefined {
    if (!state) return undefined;
    this.update(state);
    if (!this.premove || toMove(state) !== this.me) return undefined;
    const move = this.premove;
    this.premove = null;
    return legalChessMoves(state, move.from).find((m) => m.to === move.to && m.promotion === move.promotion);
  }

  /** A new match: forget selection, pending move and the cached position. */
  reset(): void {
    this.input.reset();
    this.drawings.clear();
    this.premove = null;
    this.layout = null;
    this.position = null;
    this.state = null;
    this.lastPhase = null;
    this.lastFen = null;
    this.entryCue = new ChessEntryCue();
    this.entryActive = false;
  }

  /** Lets go of the screen (the match is gone): pointers back to the pads, picker closed. */
  detach(): void {
    this.reset();
    this.host.setBoard(null);
    if (this.promotionShown) {
      this.promotionShown = false;
      this.host.hidePromotion();
    }
  }
}
